/**
 * Pure helpers shared by the bank-connection dialogs.
 *
 * Everything here is deliberately free of React so every surface that opens one
 * of them — the feed workspace, the Bank feed section of Edit Account — agrees
 * on the encoding of an ASPSP option value, the shape of the post-redirect
 * return path, how a bank account is described, and which account each side of
 * a link should pre-select.
 */
import { formatAccountLabel, isCreditCardType, type AspspWire, type BankConnectionAccountWire } from '@releve/shared';

/* ------------------------------------------------------------------------- *
 * The resume marker
 * ------------------------------------------------------------------------- */

/**
 * Query parameter the bank returns the user with, telling the workspace to
 * reopen a dialog instead of sitting there looking unchanged.
 *
 * Its VALUE is the ledger (feed) account id the flow started from, not the
 * connection id: `bank_connections.id` does not exist yet when the browser
 * builds `return_to` (the row is created by the authorize route, which echoes
 * `return_to` back verbatim), whereas the ledger account is exactly what the
 * workspace needs in order to know whose Dialog B to reopen.
 */
export const CONNECT_RESUME_PARAM = 'connect';

/**
 * The one marker value that is NOT a ledger account id: the flow started from
 * the page-level "Connect a bank" button, with no ledger account in existence
 * yet, so the return opens Dialog C ("set up the bank's accounts") instead of
 * Dialog B. Ledger ids are UUIDs, so `new` can never collide with one.
 *
 * Nothing server-side inspects the marker's value — the authorize route and the
 * callback page only assert the `/org/<orgId>/` prefix — so this sentinel needs
 * no migration, route or schema change.
 */
export const CONNECT_RESUME_NEW = 'new';

/**
 * The org-relative path the bank should return the user to — the CURRENT
 * location including its query string, plus the resume marker.
 *
 * The query string is part of the value on purpose: two surfaces host the feed
 * tab (`/accounting/bank-accounts` and `/accounting/credit-cards`, both of them
 * `AccountFeedWorkspace`), both bookmarkable via `?account=` / `?tab=`, so a
 * credit-card connection has to come back to the credit-cards page with the
 * same account selected.
 *
 * The backend re-asserts that this starts with `/org/<orgId>/` and the callback
 * page asserts it again before navigating; this function only builds it.
 *
 */
export function buildConnectReturnPath(
  pathname: string,
  search: string,
  bankAccountId: string,
): string {
  const params = new URLSearchParams(search);
  params.set(CONNECT_RESUME_PARAM, bankAccountId);
  return `${pathname}?${params.toString()}`;
}

/**
 * The route segment `accountTypeName` is listed on. Feed accounts are only ever
 * Bank or Credit Cards (`isFeedAccountType`), and `AccountFeedWorkspace` mounts
 * at exactly one page per type, so the two are the whole mapping.
 */
function feedPageSegment(accountTypeName: string): 'bank-accounts' | 'credit-cards' {
  return isCreditCardType(accountTypeName) ? 'credit-cards' : 'bank-accounts';
}

/**
 * Return path for a connect flow that starts with NO ledger account — the
 * page-level "Connect a bank" button. The marker is {@link CONNECT_RESUME_NEW},
 * which is what makes the return open Dialog C rather than Dialog B.
 *
 * Built from the org base path and the account type rather than from
 * `location`, so the flow is explicit about where it lands: Dialog A is hosted
 * from surfaces (Edit Account's Bank feed section) whose own URL is not the
 * workspace that handles `connect=`.
 */
export function buildNewAccountReturnPath(
  orgBasePath: string,
  accountTypeName: string,
): string {
  const params = new URLSearchParams();
  // No `?account=`: nothing is selected yet, and the workspace picks the first
  // account once Dialog C has created one.
  params.set('tab', 'feed');
  params.set(CONNECT_RESUME_PARAM, CONNECT_RESUME_NEW);
  return `${orgBasePath}/accounting/${feedPageSegment(accountTypeName)}?${params.toString()}`;
}

/**
 * Return path for a connect/reconnect flow that started from an EXISTING ledger
 * account. Selects it (`?account=`) on the feed tab and carries its id as the
 * resume marker, so the return reopens Dialog B against that account.
 */
export function buildAccountReturnPath(
  orgBasePath: string,
  accountTypeName: string,
  ledgerAccountId: string,
): string {
  const params = new URLSearchParams();
  params.set('account', ledgerAccountId);
  params.set('tab', 'feed');
  params.set(CONNECT_RESUME_PARAM, ledgerAccountId);
  return `${orgBasePath}/accounting/${feedPageSegment(accountTypeName)}?${params.toString()}`;
}

/* ------------------------------------------------------------------------- *
 * The step model
 * ------------------------------------------------------------------------- */

/**
 * Which connect journey the user is on. This is the ONE answer to "which step am
 * I on", shared by the three dialogs and the org-less callback page — each of
 * which previously carried its own `FLOW_STEP_COUNT = 3`.
 *
 * - `new`       — bank-first: the page-level "Connect a bank" button. Ends in
 *                 Dialog C, which lists what the bank returned and then RUNS the
 *                 batch — two steps, not one (see {@link connectFlowSteps}).
 * - `link`      — account-first, and that ledger account is not fed yet. Ends in
 *                 Dialog B, the account picker.
 * - `reconnect` — account-first, and that ledger account is ALREADY fed. Ends at
 *                 the bank: the resume effect strips the marker and opens
 *                 nothing, so this journey is genuinely TWO steps.
 *
 * The third case is not an edge case — it is every PSD2 consent renewal, the
 * most frequent journey there is. A fixed three would leave the header of the
 * first dialog promising a step that never arrives.
 */
export type ConnectFlowJourney = 'new' | 'link' | 'reconnect';

/** One entry of the journey's step strip. The label is a key, never text. */
export interface ConnectFlowStep {
  /** Stable key — also the React key once it reaches `StepIndicator`. */
  key: string;
  /** i18n key for the label; this module stays React- and i18n-free. */
  labelKey: string;
}

function flowStep(key: string): ConnectFlowStep {
  return { key, labelKey: `bankAccounts.sync.flowSteps.${key}` };
}

/**
 * The steps of one journey, in display order — TWO, THREE OR FOUR of them, which
 * is the whole point (see {@link ConnectFlowJourney}).
 *
 * This is what feeds `StepIndicator`'s `steps` prop, whose contract is that the
 * CALLER filters. There is deliberately no exported total: the total is
 * `steps.length`, and a surface names its position by KEY (`ConnectFlowHeader`
 * resolves it) rather than by an index it would have to keep in sync.
 *
 * `chooseAccount` and `setUp` are alternative THIRD labels. `finish` is a fourth
 * step the bank-first journey alone has: deciding what happens to each account
 * and WATCHING the batch run are genuinely two screens — the run is N creates +
 * N links with no transaction behind it, it is the one moment the window must
 * stay open, and the user has nothing to do while it happens.
 */
export function connectFlowSteps(journey: ConnectFlowJourney): ConnectFlowStep[] {
  const opening = [flowStep('connect'), flowStep('authorize')];
  if (journey === 'reconnect') return opening;
  if (journey === 'link') return [...opening, flowStep('chooseAccount')];
  return [...opening, flowStep('setUp'), flowStep('finish')];
}

/**
 * Query parameter carrying the journey's step COUNT on the return path.
 *
 * WHY A STAMP AT ALL. `/bank-callback` is org-less: it has no connections query,
 * so it cannot tell a `link` journey from a `reconnect` one and used to show a
 * step only for `connect=new`. Every account-first connect therefore read
 * "1 of 3" -> *nothing* -> "3 of 3", with the middle step — the one the user is
 * actually staring at — missing. The count is resolved once, where the
 * connections are (`useBankFeedConnectionFlow`'s `openConnect`), and written
 * here for the callback to read back.
 *
 * COSMETIC ONLY, NEVER A TRUST BOUNDARY. `return_to` rides inside the signed
 * `state` payload, but the callback reads an UNVERIFIED decode of it (the HMAC
 * key never leaves the backend). A tampered stamp can therefore only mislabel a
 * progress strip; nothing reads it to decide what to do.
 */
export const CONNECT_FLOW_STEPS_PARAM = 'connect_steps';

/** Split a return path into `[pathname, params]`, tolerating a missing query. */
function splitReturnPath(returnTo: string): [string, URLSearchParams] {
  const queryStart = returnTo.indexOf('?');
  if (queryStart < 0) return [returnTo, new URLSearchParams()];
  return [returnTo.slice(0, queryStart), new URLSearchParams(returnTo.slice(queryStart + 1))];
}

/**
 * Write the journey's step count onto a return path. Stamped EXPLICITLY FOR
 * EVERY JOURNEY (`2`, `3` and `4`) rather than "the usual one unless absent", so
 * that the one state shape that can never carry a stamp — one minted before this
 * shipped, still alive for up to its 30-minute TTL — reads back as "no idea"
 * rather than as a guess.
 */
export function stampConnectFlowJourney(returnTo: string, journey: ConnectFlowJourney): string {
  const [pathname, params] = splitReturnPath(returnTo);
  params.set(CONNECT_FLOW_STEPS_PARAM, String(connectFlowSteps(journey).length));
  return `${pathname}?${params.toString()}`;
}

/**
 * Recover the journey from a stamped return path, or null when there is nothing
 * honest to say — no path, no stamp (a pre-deploy state), or a stamp we did not
 * write. Callers render no step strip on null.
 *
 * The stamp gives the COUNT; the resume marker beside it distinguishes the two
 * three-step journeys, and so which third label to show.
 */
export function readConnectFlowJourney(
  returnTo: string | null | undefined,
): ConnectFlowJourney | null {
  if (!returnTo) return null;
  const [, params] = splitReturnPath(returnTo);
  const stamp = params.get(CONNECT_FLOW_STEPS_PARAM);
  const marker = params.get(CONNECT_RESUME_PARAM);
  if (stamp === '2') return 'reconnect';
  // Four steps is the bank-first journey and nothing else.
  if (stamp === '4') return marker === CONNECT_RESUME_NEW ? 'new' : null;
  if (stamp !== '3') return null;
  // A LEGACY three that names itself bank-first: minted while Dialog C's run was
  // still part of step 3, and valid for the rest of its 30-minute TTL. The
  // journey is the same one; only its length changed, and the length comes from
  // `connectFlowSteps`, never from the stamp.
  if (marker === CONNECT_RESUME_NEW) return 'new';
  // A three-step journey with no marker cannot happen — every builder above sets
  // one — so treat it as the tampered/garbled case and say nothing.
  return marker ? 'link' : null;
}

/* ------------------------------------------------------------------------- *
 * ASPSP option values
 * ------------------------------------------------------------------------- */

/**
 * `(name, country)` IS the bank's identity in every Enable Banking call — there
 * is no bank id — so a single `<SearchableSelect>` option value has to carry
 * both. The country code is a fixed two letters, so a leading `XX:` prefix is
 * unambiguous even for bank names containing a colon.
 */
export function encodeAspspValue(aspsp: { name: string; country: string }): string {
  return `${aspsp.country}:${aspsp.name}`;
}

/** Inverse of {@link encodeAspspValue}; null when the value is not one of ours. */
export function decodeAspspValue(
  value: string | undefined | null,
): { name: string; country: string } | null {
  if (!value) return null;
  const separator = value.indexOf(':');
  if (separator !== 2) return null;
  const country = value.slice(0, separator);
  const name = value.slice(separator + 1);
  if (!/^[A-Z]{2}$/.test(country) || name.length === 0) return null;
  return { name, country };
}

/** The bank in `aspsps` matching an encoded option value, if it is still there. */
export function findAspsp(aspsps: AspspWire[], value: string | undefined): AspspWire | null {
  const decoded = decodeAspspValue(value);
  if (!decoded) return null;
  return (
    aspsps.find((a) => a.name === decoded.name && a.country === decoded.country) ?? null
  );
}

/* ------------------------------------------------------------------------- *
 * Countries
 * ------------------------------------------------------------------------- */

/**
 * Countries Enable Banking covers (EEA + the UK). `organizations.country` is
 * nullable and unset for most orgs — it is written only from Organization
 * Settings — so this control is real UI rather than a defaulted-away one, and
 * it is also how an NL org connects a Belgian account.
 */
export const ENABLE_BANKING_COUNTRIES = [
  'AT', 'BE', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES',
  'FI', 'FR', 'GB', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI',
  'LT', 'LU', 'LV', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE',
  'SI', 'SK',
] as const;

/** True when a stored `organizations.country` is usable as a default here. */
export function isSupportedCountryCode(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^[A-Z]{2}$/.test(value);
}

/**
 * A country's name in the user's language. `Intl.DisplayNames` keeps ~30
 * country names out of the translation files; the code itself is the fallback
 * on a runtime without the region data.
 */
export function countryLabel(code: string, language: string): string {
  try {
    const names = new Intl.DisplayNames([language || 'en'], { type: 'region' });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

/** The option list for the country control, sorted by the localized label. */
export function countryOptions(
  language: string,
  extraCode?: string | null,
): Array<{ value: string; label: string; keywords: string[] }> {
  const codes = new Set<string>(ENABLE_BANKING_COUNTRIES);
  if (isSupportedCountryCode(extraCode)) codes.add(extraCode);
  return Array.from(codes, (code) => ({
    value: code,
    label: countryLabel(code, language),
    // The code itself stays searchable: people type "NL" as often as "Netherlands".
    keywords: [code],
  })).sort((a, b) => a.label.localeCompare(b.label, language || 'en'));
}

/* ------------------------------------------------------------------------- *
 * Which bank account to pre-select
 * ------------------------------------------------------------------------- */

/**
 * Why an account cannot be linked to the ledger account in question. Both
 * reasons are ALSO enforced by the link route (and the currency one treats
 * `XXX` the same way); this is the readable version, shown before the user
 * spends a click on a 400.
 */
export type LinkBlockReason = 'linkedElsewhere' | 'currencyMismatch';

/**
 * `XXX` is a MULTI-CURRENCY account — one account returning several balances.
 * It never equals a ledger currency, so plain equality would reject every one
 * of them permanently; it matches anything and the ledger account defines the
 * pairing.
 */
export function currenciesCompatible(
  bankCurrency: string,
  ledgerCurrency: string | null | undefined,
): boolean {
  const bank = bankCurrency.toUpperCase();
  if (bank === 'XXX') return true;
  if (!ledgerCurrency) return true;
  return bank === ledgerCurrency.toUpperCase();
}

export function linkBlockReason(
  account: Pick<BankConnectionAccountWire, 'currency' | 'chart_of_accounts_id'>,
  ledgerAccountId: string,
  ledgerCurrency: string | null | undefined,
): LinkBlockReason | null {
  if (account.chart_of_accounts_id && account.chart_of_accounts_id !== ledgerAccountId) {
    return 'linkedElsewhere';
  }
  if (!currenciesCompatible(account.currency, ledgerCurrency)) return 'currencyMismatch';
  return null;
}

/**
 * Lowercased, accent-folded word tokens.
 *
 * UNICODE-AWARE, and accents are folded rather than dropped: the old
 * `[^a-z0-9]` scrub turned "Épargne" into "pargne", so "Compte Épargne" and
 * "Compte Epargne" — the same account, typed twice — scored 0.5 and missed.
 */
function words(value: string): string[] {
  return (
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .match(/[\p{L}\p{N}]+/gu) ?? []
  );
}

/**
 * Tokens for the similarity SCORE, which drops one-character tokens as noise.
 *
 * Never use this to decide identity: "Betaalrekening 1" and "Betaalrekening 2"
 * both reduce to `betaalrekening` here, which is exactly the pair a link must
 * tell apart. {@link normalizeName} keeps every token for that reason.
 */
function tokenize(value: string): string[] {
  return words(value).filter((token) => token.length > 1);
}

/**
 * 0…1 overlap between two names. Deliberately crude: this only ever ORDERS a
 * pre-selection the user must still confirm — the plan is explicit that the
 * first link is a manual pick, because `chart_of_accounts` holds no IBAN, BIC
 * or external account id to match on.
 */
export function nameSimilarity(a: string, b: string): number {
  return overlap(tokenize(a), tokenize(b));
}

/**
 * Dice coefficient over two token lists, 0…1. BOTH sides are de-duplicated:
 * counting an array against a Set let a repeated word push the result past 1
 * ("Bank Bank Account" vs "Bank Account" scored 1.2), which the docstring above
 * promises it cannot and which inflated `pairScore`'s name term.
 */
function overlap(a: string[], b: string[]): number {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size === 0 || right.size === 0) return 0;
  let shared = 0;
  for (const token of left) if (right.has(token)) shared += 1;
  return (2 * shared) / (left.size + right.size);
}

/**
 * The similarity used for IDENTITY, over every token including one-character
 * ones. `nameSimilarity` drops those (they are noise in a ranking), which would
 * score "Betaalrekening 1" against "Betaalrekening 2" as a perfect 1.0.
 */
function identitySimilarity(a: string, b: string): number {
  return overlap(words(a), words(b));
}

/** The ledger side of a suggestion — the columns the scoring reads, nothing more. */
export interface SuggestableLedgerAccount {
  id: string;
  name: string;
  code?: string | null;
  currency: string | null;
  /**
   * `chart_of_accounts.account_description`. Optional because only the
   * evidence test below reads it: an IBAN a bookkeeper typed into the account's
   * description is the strongest identity signal the chart of accounts has —
   * the table holds no IBAN, BIC or external id column of its own.
   */
  description?: string | null;
}

/** The bank side of a suggestion. */
type SuggestableBankAccount = Pick<
  BankConnectionAccountWire,
  'currency' | 'chart_of_accounts_id' | 'iban' | 'display_name'
>;

/**
 * How well a (bank account, ledger account) pair go together. Currency
 * compatibility dominates (an exact match beats `XXX`), then the account number
 * / IBAN tail appearing in the ledger account's name or code, then plain name
 * overlap.
 *
 * One function so the two directions below cannot drift apart: Dialog B picks a
 * bank account for a known ledger account, Dialog C picks a ledger account for a
 * known bank account, and a user who opens both should not be told two different
 * things about the same pair.
 */
function pairScore(bank: SuggestableBankAccount, ledger: SuggestableLedgerAccount): number {
  let score = 0;
  const bankCurrency = bank.currency.toUpperCase();
  if (ledger.currency && bankCurrency === ledger.currency.toUpperCase()) score += 3;
  else if (bankCurrency === 'XXX') score += 1;

  const ledgerText = `${ledger.name} ${ledger.code ?? ''}`;
  const tail = (bank.iban ?? '').replace(/\s+/g, '').slice(-4);
  if (tail.length === 4 && ledgerText.includes(tail)) score += 3;

  score += 2 * nameSimilarity(bank.display_name ?? '', ledgerText);
  return score;
}

/**
 * Best-guess bank account for a ledger account, or null when nothing is
 * linkable.
 */
export function pickSuggestedAccountId(
  accounts: BankConnectionAccountWire[],
  ledger: SuggestableLedgerAccount,
): string | null {
  let bestId: string | null = null;
  let bestScore = -1;

  for (const account of accounts) {
    if (linkBlockReason(account, ledger.id, ledger.currency)) continue;

    const score = pairScore(account, ledger);
    if (score > bestScore) {
      bestScore = score;
      bestId = account.id;
    }
  }

  return bestId;
}

/* ------------------------------------------------------------------------- *
 * Is an existing ledger account OBVIOUSLY this bank account?
 * ------------------------------------------------------------------------- */

/** What made a bank account and a ledger account an obvious pair. */
export type LedgerMatchReason = 'iban' | 'name';

/** An existing ledger account Dialog C may pre-select, and why. */
export interface ConfidentLedgerMatch {
  id: string;
  reason: LedgerMatchReason;
}

/**
 * Comparable form of a name — EVERY token, including one-character ones.
 *
 * `tokenize` drops those as noise, which is right for a score and wrong for
 * identity: an org with `Betaalrekening 1` and `Betaalrekening 2` would have
 * both reduce to the same string and the bank's `Betaalrekening 1` would match
 * whichever the chart of accounts happened to return first.
 */
function normalizeName(value: string): string {
  return words(value).join(' ');
}

/** Letters and digits only, uppercased — how a full IBAN is compared. */
function normalizeIdentifier(value: string): string {
  return value.replace(/[^a-z0-9]/gi, '').toUpperCase();
}

/** The digit runs in a string, each one whole — `"Kantoor 1017 AB"` -> `["1017"]`. */
function digitRuns(value: string): string[] {
  return value.match(/\d+/g) ?? [];
}

/**
 * Does `text` carry this bank account's number in a form that can only be an
 * ACCOUNT NUMBER, rather than four digits that happen to coincide?
 *
 * Three accepted shapes, in descending strength:
 *
 * 1. the whole IBAN, normalised (15+ characters — unambiguous anywhere);
 * 2. a digit run of six or more that the IBAN ends with — a bookkeeper writing
 *    a recognisable chunk of the number;
 * 3. the last four **behind the `••••` mask this app itself writes**
 *    (`defaultAccountName` -> "Betaalrekening ••••3011"), which is how an
 *    account created by this dialog is recognised on a later reconnect.
 *
 * What is deliberately NOT accepted is a bare four-digit run: a Dutch postcode
 * ("Kantoor 1017 AB"), a year ("Opened in 2019") and a chart-of-accounts number
 * are all four digits, and treating them as identity evidence pre-selects a
 * link into somebody else's account — the one mistake this whole helper exists
 * to avoid.
 */
const MASKED_TAIL = /[•*x]{2,}\s*(\d{4})/giu;
const MIN_PARTIAL_IBAN_DIGITS = 6;

function carriesAccountNumber(text: string, iban: string): boolean {
  if (!text.trim() || iban.length < 4) return false;
  if (normalizeIdentifier(text).includes(iban)) return true;

  const tail = iban.slice(-4);
  for (const run of digitRuns(text)) {
    if (run.length >= MIN_PARTIAL_IBAN_DIGITS && iban.endsWith(run)) return true;
  }
  for (const masked of text.matchAll(MASKED_TAIL)) {
    if (masked[1] === tail) return true;
  }
  return false;
}

/**
 * How alike two names have to be to count as "the same account". Tuned to admit
 * word-order and punctuation differences ("Rabobank betaalrekening" vs
 * "Betaalrekening Rabobank") and to reject a shared generic word
 * ("Savings" vs "Savings Account" scores 0.67).
 */
const CONFIDENT_NAME_SIMILARITY = 0.8;

/**
 * Evidence that `ledger` IS the ledger account for `bank`, or null.
 *
 * DELIBERATELY NOT A RANKING. `pairScore` answers "which of these is the best
 * guess", which is the right question for Dialog B — the user has already
 * decided to link, so *something* must be pre-selected. Dialog C asks a
 * different question: create a new account, or reuse an existing one? Defaulting
 * that to "reuse" on a best-guess means a bank feed silently pointed at the
 * wrong account, and the statement lines that follow are far harder to unpick
 * than a spare empty account. So the default is CREATE unless one of two things
 * is actually true:
 *
 * 1. `iban` — the ledger account's name or description carries this account's
 *    number in a form that can only be an account number
 *    ({@link carriesAccountNumber}). The name and the description are tested
 *    SEPARATELY: concatenating them let a tail straddle the join, so
 *    "Rekening 20" + "24 uur service" matched a `…2024` IBAN.
 * 2. `name` — the two names are the same once case, accents, punctuation and
 *    word order are taken out, or they overlap above
 *    {@link CONFIDENT_NAME_SIMILARITY}.
 */
export function ledgerMatchReason(
  bank: SuggestableBankAccount,
  ledger: SuggestableLedgerAccount,
): LedgerMatchReason | null {
  const iban = normalizeIdentifier(bank.iban ?? '');
  // No IBAN at all (the bank identifies the account some other way) falls
  // straight through to the name arm — `includes('')` is true for everything.
  if (iban.length >= 4) {
    const carried =
      carriesAccountNumber(ledger.name, iban) ||
      carriesAccountNumber(ledger.description ?? '', iban) ||
      // The CODE is only ever read for a WHOLE IBAN — a four-digit number there
      // is a chart-of-accounts number, not a bank one.
      normalizeIdentifier(ledger.code ?? '').includes(iban);
    if (carried) return 'iban';
  }

  const bankName = normalizeName(bank.display_name ?? '');
  const ledgerName = normalizeName(ledger.name);
  if (!bankName || !ledgerName) return null;
  if (bankName === ledgerName) return 'name';
  if (identitySimilarity(bankName, ledgerName) >= CONFIDENT_NAME_SIMILARITY) return 'name';
  return null;
}

/**
 * The one existing ledger account Dialog C should pre-select for a bank account,
 * or null — which is the ordinary answer, and means "create a new one".
 *
 * `candidates` is the caller's already-filtered list (active, feed-typed,
 * currency-compatible, not claimed by any connection, not taken by an earlier
 * row).
 *
 * A TIE IS NOT CONFIDENCE. If two candidates carry the same evidence, this
 * returns null rather than ranking them: two accounts both named
 * "Betaalrekening" (the bank's product name, which every current account there
 * shares) used to resolve to whichever the chart-of-accounts query returned
 * first, which is a coin flip deciding where a bank feed points. The stronger
 * evidence still wins outright — one IBAN match beats any number of name
 * matches, because a number identifies an account and a name describes it.
 */
export function findConfidentLedgerMatch(
  candidates: SuggestableLedgerAccount[],
  bankAccount: SuggestableBankAccount,
): ConfidentLedgerMatch | null {
  const byReason: Record<LedgerMatchReason, string[]> = { iban: [], name: [] };

  for (const ledger of candidates) {
    if (linkBlockReason(bankAccount, ledger.id, ledger.currency)) continue;
    const reason = ledgerMatchReason(bankAccount, ledger);
    if (reason) byReason[reason].push(ledger.id);
  }

  // Order matters: `iban` before `name`.
  for (const reason of ['iban', 'name'] as const) {
    const ids = byReason[reason];
    if (ids.length === 1) return { id: ids[0], reason };
    if (ids.length > 1) return null;
  }
  return null;
}

/* ------------------------------------------------------------------------- *
 * Describing a bank account
 * ------------------------------------------------------------------------- */

/**
 * `t` from `useTranslation`, narrowed to what the helpers below call. Typed
 * structurally rather than imported so this file stays React-free.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * IBAN · name · currency, with a translated stand-in for each missing half.
 * Both halves are nullable on the wire, and an account with neither still has to
 * be distinguishable from its siblings in a radio list.
 */
export function describeAccount(account: BankConnectionAccountWire, t: Translate): string {
  const name = account.display_name ?? t('bankAccounts.sync.chooseAccount.unnamed');
  const iban = account.iban ?? t('bankAccounts.sync.chooseAccount.noIban');
  return `${iban} · ${name} · ${account.currency}`;
}

/**
 * How a LEDGER account is named on screen in the bank-connect dialogs: the
 * shared `formatAccountLabel` (`code name`, or the bare name), so there is one
 * label format app-wide. A blank-padded number counts as none.
 *
 * `showAccountNumbers` is the org preference (`useShowAccountNumbers()`):
 * false → the bare name. (The number still feeds the ledger MATCHING, which is
 * not a display.)
 */
export function describeLedgerAccount(
  account: {
    account_number?: string | null;
    account_name: string;
  },
  showAccountNumbers = true,
): string {
  return formatAccountLabel(
    { account_number: account.account_number?.trim() || null, account_name: account.account_name },
    showAccountNumbers,
  );
}

/* ------------------------------------------------------------------------- *
 * Leaving the SPA
 * ------------------------------------------------------------------------- */

/**
 * Full-page navigation to the bank. Isolated in one function because it is the
 * moment the SPA unloads — which is exactly why connecting and linking are two
 * dialogs rather than one wizard — and because jsdom cannot navigate, so tests
 * mock this rather than the whole submit path.
 */
export function redirectToBank(url: string): void {
  window.location.assign(url);
}
